import isString from './isString';

const ROOT = 'https://media.trmedia.se';

/**
 * 100x50 -> g_xy_center,x_100,y_50
 *
 * @param  focus            focus string
 * @param  fallback         Set to true to fallback to center crop or a custom transform string.
 *                          Defaults to false.
 */
export const getFocusGravity = (
  focus: string | null,
  fallback: boolean | string = false,
): string[] => {
  if (!focus?.includes('x')) {
    return isString(fallback)
      ? [fallback as string]
      : fallback
        ? ['g_center']
        : [];
  }
  const [x, y] = focus.split('x');
  return ['g_xy_center', `x_${x}`, `y_${y}`];
};

/**
 * @param  imageId             Cloudinary image id
 * @param  width               Width to scale image to. Optional but if omitted it is recommended
 *                             to use a height transform instead.
 * @param  aspectRatio         Optional ratio to crop to, array of two numbers or
 *                             string with two numbers separated by ':'
 * @param  transformParameters Optional transforms in addition to or overriding the defaults. See
 *                             https://cloudinary.com/documentation/image_transformation_reference.
 *                             By default the following transforms are set unless overriden:
 *                             crop: c_fill
 *                             format: f_auto
 *                             flag: fl_lossy
 * @param  rootUrl             Optional override the default root url
 */
export function getUrl(
  imageId: undefined | null,
  width?: number,
  aspectRatio?: [number, number] | string | null,
  transformParameters?: string[],
  rootUrl?: string,
): undefined;
export function getUrl(
  imageId: string,
  width?: number,
  aspectRatio?: [number, number] | string | null,
  transformParameters?: string[],
  rootUrl?: string,
): string;
export function getUrl(
  imageId: string | undefined | null,
  width?: number,
  aspectRatio?: [number, number] | string | null,
  transformParameters: string[] = [],
  rootUrl: string = ROOT,
) {
  if (imageId == null) {
    return;
  }

  if (width == null && !transformParameters.some(p => p.startsWith('h_'))) {
    console.warn(
      `
        Neither width or height ('h_xxx' transform) for image '${imageId}' has been specified.
        This will load the original size, a potential waste of bandwidth.
      `,
    );
  }

  const ratioArray: (number | string)[] | null = Array.isArray(aspectRatio)
    ? aspectRatio
    : isString(aspectRatio)
      ? aspectRatio.split(':')
      : null;

  let ratioTransform;
  if (
    ratioArray != null &&
    ratioArray.length === 2 &&
    ratioArray.every(Number.isInteger)
  ) {
    ratioTransform = `ar_${ratioArray.join(':')}`;
  }

  if (aspectRatio != null && ratioTransform == null) {
    throw new Error(
      `
        '${aspectRatio}' is not a valid aspectRatio value for image
        '${imageId}'. Expected an array of two integers or a string
        with two numbers separated by ':'
      `,
    );
  }

  const transforms = [
    // Scale to desired width
    width != null ? `w_${Math.round(width)}` : undefined,
    // Ratio crop
    ratioTransform,
    // Crop mode, default to fill
    transformParameters.some(p => p.startsWith('c_')) ? undefined : 'c_fill',
    // File format, default to let Cloudinary decide optimal image format
    transformParameters.some(p => p.startsWith('f_')) ? undefined : 'f_auto',
    // Let Cloudinary auto-convert png to jpg or webp if possible. This saves bandwidth
    transformParameters.some(p => p.startsWith('f_') || p.startsWith('fl_'))
      ? undefined
      : 'fl_lossy',
    // Quality, default to let Cloudinary decide optimal compression
    transformParameters.some(p => p.startsWith('q_')) ? undefined : 'q_auto',
    // Custom transformations
    ...transformParameters,
  ]
    .filter(Boolean)
    .join(',');
  return `${rootUrl}/${transforms}/${imageId}`;
}
